import axios, {
    AxiosError,
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
} from 'axios';
import { HttpError, HttpResponse, RequestConfig } from './types';
import {
  isAuthPublicPath,
  notifySessionExpired,
} from '../../utils/authSessionHandler';
import { secureStorage } from '../../utils/secureStorage';

export interface IHttpClient {
  get<T>(url: string, config?: RequestConfig): Promise<HttpResponse<T>>;
  post<T>(
    url: string,
    data?: any,
    config?: RequestConfig
  ): Promise<HttpResponse<T>>;
  put<T>(
    url: string,
    data?: any,
    config?: RequestConfig
  ): Promise<HttpResponse<T>>;
  delete<T>(url: string, config?: RequestConfig): Promise<HttpResponse<T>>;
  patch<T>(
    url: string,
    data?: any,
    config?: RequestConfig
  ): Promise<HttpResponse<T>>;
}

export class HttpClient implements IHttpClient {
  private axiosInstance: AxiosInstance;
  private baseURL: string;

  constructor(baseURL: string, defaultConfig?: AxiosRequestConfig) {
    this.baseURL = baseURL;
    this.axiosInstance = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
      ...defaultConfig,
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      config => {
        // Add auth token if available
        const token = secureStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => {
        return Promise.reject(this.handleError(error));
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error: AxiosError) => {
        const httpError = this.handleError(error);
        if (httpError.status === 401) {
          const requestUrl = String(error.config?.url || '');
          if (!isAuthPublicPath(requestUrl)) {
            notifySessionExpired('unauthorized');
          }
        }
        return Promise.reject(httpError);
      }
    );
  }

  private handleError(error: AxiosError): HttpError {
    if (error.response) {
      // Server responded with error status
      const responseData = error.response.data as any;
      // Priorizar detail sobre message
      const errorMessage = responseData?.detail || responseData?.message || error.message;
      return {
        message: errorMessage,
        status: error.response.status,
        data: error.response.data,
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        message: 'Network error - no response received',
        status: 0,
      };
    } else {
      // Something else happened
      return {
        message: error.message,
      };
    }
  }

  async get<T>(url: string, config?: RequestConfig): Promise<HttpResponse<T>> {
    const axiosConfig: AxiosRequestConfig = {
      params: config?.params,
      headers: config?.headers,
      timeout: config?.timeout,
    };
    const response = await this.axiosInstance.get<T>(url, axiosConfig);
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers as Record<string, string>,
    };
  }

  async post<T>(
    url: string,
    data?: any,
    config?: RequestConfig
  ): Promise<HttpResponse<T>> {
    const axiosConfig: AxiosRequestConfig = {
      headers: config?.headers,
      timeout: config?.timeout,
    };
    const response = await this.axiosInstance.post<T>(url, data, axiosConfig);
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers as Record<string, string>,
    };
  }

  async put<T>(
    url: string,
    data?: any,
    config?: RequestConfig
  ): Promise<HttpResponse<T>> {
    const axiosConfig: AxiosRequestConfig = {
      headers: config?.headers,
      timeout: config?.timeout,
    };
    const response = await this.axiosInstance.put<T>(url, data, axiosConfig);
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers as Record<string, string>,
    };
  }

  async delete<T>(
    url: string,
    config?: RequestConfig
  ): Promise<HttpResponse<T>> {
    const axiosConfig: AxiosRequestConfig = {
      headers: config?.headers,
      timeout: config?.timeout,
    };
    const response = await this.axiosInstance.delete<T>(url, axiosConfig);
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers as Record<string, string>,
    };
  }

  async patch<T>(
    url: string,
    data?: any,
    config?: RequestConfig
  ): Promise<HttpResponse<T>> {
    const axiosConfig: AxiosRequestConfig = {
      headers: config?.headers,
      timeout: config?.timeout,
    };
    const response = await this.axiosInstance.patch<T>(url, data, axiosConfig);
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers as Record<string, string>,
    };
  }

  // Method for file uploads
  async upload<T>(
    url: string,
    formData: FormData,
    config?: RequestConfig
  ): Promise<HttpResponse<T>> {
    const axiosConfig: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config?.headers,
      },
      timeout: config?.timeout || 60000, // Longer timeout for uploads
    };
    const response = await this.axiosInstance.post<T>(
      url,
      formData,
      axiosConfig
    );
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers as Record<string, string>,
    };
  }

  // Method for streaming responses
  async stream(
    url: string,
    data?: any,
    config?: RequestConfig
  ): Promise<ReadableStream> {
    const fullUrl = `${this.baseURL}${url}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...config?.headers,
    };

    // Add auth token if available
    const token = secureStorage.getItem('access_token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(fullUrl, {
      method: 'POST',
      headers,
      body: data ? JSON.stringify(data) : undefined,
      signal: config?.signal,
    });

    if (!response.ok) {
      if (response.status === 401 && !isAuthPublicPath(url)) {
        notifySessionExpired('unauthorized');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    return response.body;
  }
}
